import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  LocationOn,
  Schedule,
  Close,
  DirectionsCar
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { generateAgendaConflictMessage, isMatchTBD } from '@/utils/conflictFormatter';
import { formatInUserTimezone } from '@/utils/timezoneUtils';
import { RootState } from '@/store';
import { TeamLogo } from './TeamLogo';
import BottomDrawer from './BottomDrawer';

export interface AgendaMatch {
  id: string;
  dateTime: string | null;
  homeTeam: string;
  awayTeam: string;
  isHome: boolean;
  location?: string;
  league: string;
  division?: string;
  player: string;
  playerColor: string;
  players?: { name: string; color: string; isHome: boolean }[];
  hasConflict: boolean;
  conflictSeverity?: string;
  status?: string;
  homeTeamLogo?: { logoPath?: string; logoUrl?: string };
  awayTeamLogo?: { logoPath?: string; logoUrl?: string };
  departureTime?: {
    departureTime: string | null;
    isApproximate: boolean;
    travelTimeMinutes: number;
    noTravelRequired?: boolean;
    origin: {
      type: 'home' | 'previous_match' | 'other_player_match';
      location: string;
      playerId?: string;
    };
  };
}

interface AgendaDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string | null;
  matches: AgendaMatch[];
  getConflictBetweenMatches?: (match1: AgendaMatch, match2: AgendaMatch) => any;
}

/** Shared match list content used by both mobile drawer and desktop dialog */
const AgendaContent: React.FC<{
  groupedMatches: AgendaMatch[];
  selectedDate: string | null;
  formatMatchTime: (dateTime: string | null, status?: string) => string;
  formatDepartureTime: (departureTime: string) => string;
  formatTravelMinutes: (minutes: number) => string;
  getConflictBetweenMatches?: (match1: AgendaMatch, match2: AgendaMatch) => any;
  showHeader?: boolean;
}> = ({ groupedMatches, selectedDate, formatMatchTime, formatDepartureTime, formatTravelMinutes, getConflictBetweenMatches, showHeader }) => (
  <Box>
    {/* Header — shown in mobile drawer mode */}
    {showHeader && (
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            borderBottom: '2px solid',
            borderColor: 'text.primary',
            pb: 0.5,
          }}
        >
          {selectedDate ? dayjs(selectedDate).format('dddd, MMMM D') : ''}
        </Typography>
      </Box>
    )}

    {/* Match list */}
    <Box sx={{ px: 2.5, pb: 2 }}>
      {groupedMatches.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary" fontStyle="italic">
            No matches scheduled for this date
          </Typography>
        </Box>
      ) : (
        <Box>
          {groupedMatches.map((match, index) => {
            const players = match.players || [{ name: match.player, color: match.playerColor, isHome: match.isHome }];

            return (
              <React.Fragment key={match.id}>
                <Box sx={{ pt: index === 0 ? 0.5 : 2, pb: 2 }}>
                  {/* Time */}
                  <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Schedule sx={{ fontSize: 16 }} color="action" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatMatchTime(match.dateTime, match.status)}
                    </Typography>
                  </Box>

                  {/* Teams — stacked with logos */}
                  <Box sx={{ mb: 1.25 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                      <TeamLogo
                        teamName={match.homeTeam}
                        logoPath={match.homeTeamLogo?.logoPath}
                        logoUrl={match.homeTeamLogo?.logoUrl}
                        size={24}
                      />
                      <Typography variant="body2">
                        {match.homeTeam} <Typography component="span" variant="caption" color="text.disabled">·</Typography> <Typography component="span" variant="caption" color="text.secondary">Home</Typography>
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TeamLogo
                        teamName={match.awayTeam}
                        logoPath={match.awayTeamLogo?.logoPath}
                        logoUrl={match.awayTeamLogo?.logoUrl}
                        size={24}
                      />
                      <Typography variant="body2">
                        {match.awayTeam} <Typography component="span" variant="caption" color="text.disabled">·</Typography> <Typography component="span" variant="caption" color="text.secondary">Away</Typography>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Additional details — location, departure, player/league */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {/* Location */}
                    {match.location && (
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <LocationOn sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {match.location}
                        </Typography>
                      </Box>
                    )}

                    {/* Departure time */}
                    {match.departureTime && match.dateTime && dayjs(match.dateTime).add(2, 'hour').isAfter(dayjs()) && (
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <DirectionsCar sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {match.departureTime.noTravelRequired || (match.departureTime.departureTime === null && match.departureTime.travelTimeMinutes === 0) ? (
                            'No travel required'
                          ) : match.status === 'TBD' ? (
                            match.location && match.departureTime.travelTimeMinutes > 0
                              ? `Travel time: ${formatTravelMinutes(match.departureTime.travelTimeMinutes)}`
                              : null
                          ) : match.departureTime.departureTime ? (
                            <>
                              {match.departureTime.travelTimeMinutes > 0 && (
                                <>{match.departureTime.isApproximate ? '~' : ''}{formatTravelMinutes(match.departureTime.travelTimeMinutes)} · </>
                              )}
                              Leave {match.departureTime.origin.type === 'home' ? 'home' :
                                     match.departureTime.origin.type === 'previous_match' ? match.departureTime.origin.location :
                                     "other player's match"} by {formatDepartureTime(match.departureTime.departureTime)}
                            </>
                          ) : null}
                        </Typography>
                      </Box>
                    )}

                    {/* Player and home/away + league */}
                    <Box>
                    <Typography variant="caption" color="text.secondary">
                      {players.map((p) =>
                        players.length > 1 ? `${p.name} (${p.isHome ? 'Home' : 'Away'})` : p.name
                      ).join(', ')}
                      {players.length === 1 && ` · ${players[0].isHome ? 'Home' : 'Away'}`}
                      {' · '}{match.league}
                      {match.division && ` · ${match.division}`}
                    </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Divider or conflict indicator between matches */}
                {index < groupedMatches.length - 1 && (() => {
                  const nextMatch = groupedMatches[index + 1];
                  const conflict = getConflictBetweenMatches ? getConflictBetweenMatches(match, nextMatch) : null;

                  if (conflict) {
                    const severityColor = conflict.severity === 'DIRECT' ? '#d32f2f' :
                                          conflict.severity === 'MODERATE' ? '#ed6c02' : '#2e7d32';

                    const match1IsTBD = isMatchTBD(match);
                    const match2IsTBD = isMatchTBD(nextMatch);

                    const conflictText = generateAgendaConflictMessage(
                      {
                        severity: conflict.severity,
                        timeDifference: conflict.timeDifference || 0,
                        availableTravelWindow: conflict.availableTravelWindow,
                        travelTime: conflict.travelTime
                      },
                      match.player,
                      nextMatch.player,
                      match1IsTBD,
                      match2IsTBD,
                      match.dateTime
                    );

                    return (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        my: 0.5,
                      }}>
                        <Box sx={{ flex: '1 1 20px', height: '1px', bgcolor: severityColor }} />
                        <Typography variant="caption" sx={{
                          color: severityColor,
                          fontWeight: 500,
                          textAlign: 'center',
                          lineHeight: 1.2,
                          textWrap: 'balance',
                          flex: '0 1 auto',
                        }}>
                          {conflictText}
                        </Typography>
                        <Box sx={{ flex: '1 1 20px', height: '1px', bgcolor: severityColor }} />
                      </Box>
                    );
                  }

                  return <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />;
                })()}
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  </Box>
);

const AgendaDialog: React.FC<AgendaDialogProps> = ({
  open,
  onClose,
  selectedDate,
  matches,
  getConflictBetweenMatches
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector((state: RootState) => state.auth.user);
  const orgSettings = useSelector((state: RootState) => state.organization.settings);

  // Prevent body scroll when desktop dialog is open
  useEffect(() => {
    if (open && !isMobile) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open, isMobile]);

  const formatMatchTime = (dateTime: string | null, status?: string) => {
    if (!dateTime || status === 'TBD') return 'TBD';
    return formatInUserTimezone(dateTime, 'h:mm A', true, user, orgSettings);
  };

  const formatDepartureTimeFn = (departureTime: string) => {
    return formatInUserTimezone(departureTime, 'h:mm A', true, user, orgSettings);
  };

  const formatTravelMinutes = (minutes: number) => {
    const rounded = Math.round(minutes);
    if (rounded >= 60) {
      const hours = Math.floor(rounded / 60);
      const rem = rounded % 60;
      return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
    }
    return `${rounded} min`;
  };

  // Group matches by base match ID to deduplicate
  const groupedMatches = React.useMemo(() => {
    const groups = new Map<string, AgendaMatch>();
    for (const match of matches) {
      const baseId = match.id.replace(/-[^-]+$/, '');
      const existing = groups.get(baseId);
      if (existing) {
        const players = existing.players || [{ name: existing.player, color: existing.playerColor, isHome: existing.isHome }];
        players.push({ name: match.player, color: match.playerColor, isHome: match.isHome });
        existing.players = players;
        if (match.hasConflict) existing.hasConflict = true;
      } else {
        groups.set(baseId, {
          ...match,
          players: match.players || [{ name: match.player, color: match.playerColor, isHome: match.isHome }]
        });
      }
    }
    return Array.from(groups.values());
  }, [matches]);

  const contentProps = {
    groupedMatches,
    selectedDate,
    formatMatchTime,
    formatDepartureTime: formatDepartureTimeFn,
    formatTravelMinutes,
    getConflictBetweenMatches,
  };

  if (isMobile) {
    return (
      <BottomDrawer open={open} onClose={onClose} maxWidth={440}>
        <AgendaContent {...contentProps} showHeader />
      </BottomDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock={true}
      disableRestoreFocus={true}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: 'calc(100vh - 120px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, position: 'relative', flexShrink: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            borderBottom: '2px solid',
            borderColor: 'text.primary',
            pb: 0.5,
            pr: 5,
          }}
        >
          {selectedDate ? dayjs(selectedDate).format('dddd, MMMM D') : ''}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: 'text.secondary',
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 0, pt: 0, pb: 0, overflowY: 'auto', flexShrink: 1 }}>
        <AgendaContent {...contentProps} />
      </DialogContent>
    </Dialog>
  );
};

export default AgendaDialog;
